import { Observable, EMPTY as EMPTY_OBSERVABLE } from 'rxjs'
import { TOKENS_CAN_BE_COMPLETED } from '@aytm/app/stats/lib/render/draft-js/autocompletePlugin/lib/tokens'
import extractLongestLabels from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/suggestionsPlugin/lib/extract-longest-labels'
import processSuggestionData from '@aytm/app/stats/lib/render/draft-js/autocompletePlugin/lib/process-suggestion-data'

const escapeRegExp = string => string.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')

const createFilterByRegExp = (canBeCompleted, textBefore) => {
  if (!canBeCompleted) {
    return token => /^[a-z]+$/i.test(token)
  }

  const lastWordCI = textBefore.lastWord.toUpperCase()
  const completeWithLetter = new RegExp('^' + escapeRegExp(lastWordCI) + '([A-Z]+)$')
  const completeWithNumber = new RegExp('^' + escapeRegExp(lastWordCI) + '([0-9]+)$')

  return token =>
    lastWordCI == token || !token.startsWith(lastWordCI)
      ? false
      : /\d$/.test(token)
        ? completeWithNumber.test(token)
        : completeWithLetter.test(token)
}

const filterDuplicates = (item, position, array) => array.indexOf(item) == position

const addTransitionQuestionTokens = tokens => {
  const tokensWithTransitions = []

  for (let i = -1, prevToken = ''; i < tokens.length - 1; i++) {
    const nextToken = tokens[i + 1]
    const shouldAddTransition = nextToken.startsWith(prevToken)
      ? /\d+$/.test(nextToken) && prevToken.length < nextToken.length
      : /\d+$/.test(nextToken)

    if (shouldAddTransition) {
      tokensWithTransitions.push(nextToken.replace(/\d+$/, ''))
    }

    tokensWithTransitions.push(nextToken)
    prevToken = nextToken
  }

  return tokensWithTransitions
}

const toToken = refType => ('a' === refType ? 'ARRAYREF' : refType.toUpperCase() + 'REF')
const extractIndex = label => {
  const match = label.match(/\d+$/)
  return match && match[0] ? parseInt(match[0], 10) : 0
}

const createFilterByTokenType = (refsList, tokens) => question =>
  refsList[question] && tokens && tokens.includes(toToken(refsList[question]))
const sortQuestionLabel = (a, b) => extractIndex(a.label) - extractIndex(b.label)

export default function createQuestionSuggestion(
  textBefore,
  _,
  {
    dependencies: {
      aytmLogic: { refsList, questions },
      parserResult
    }
  }
) {
  if (!questions || !parserResult) {
    return EMPTY_OBSERVABLE
  }

  const longestQuestionLabels = extractLongestLabels(questions)

  const transformToSuggestion =
    ({ canBeCompleted, parserResult }) =>
    question => {
      const completeAfterInsert = !longestQuestionLabels.includes(question)
      const token = refsList[question] ? refsList[question].toUpperCase() + 'REF' : null
      const label = question

      return processSuggestionData('question', {
        canBeCompleted,
        token,
        label,
        parserResult,
        completeAfterInsert
      })
    }

  const createSuggestions = (tokens, canBeCompleted) => {
    const filterByRegExp = createFilterByRegExp(canBeCompleted, textBefore)
    const filterByTokenType = createFilterByTokenType(refsList, tokens)

    return addTransitionQuestionTokens(questions.filter(filterByTokenType))
      .filter(filterByRegExp)
      .filter(filterDuplicates)
      .map(transformToSuggestion({ canBeCompleted, parserResult }))
      .filter(suggestion => suggestion)
      .sort(sortQuestionLabel)
  }

  return new Observable(observer => {
    const {
      tokens: { expected, type },
      text: { nonParsed }
    } = parserResult

    const isLastTokenCompletable = TOKENS_CAN_BE_COMPLETED.includes(type.last)
    const hasNonParsedText = Boolean(nonParsed)
    const hasNonParsedTextTrimmed = Boolean(nonParsed.trim())
    const canBeCompleted = Boolean(hasNonParsedTextTrimmed || (!hasNonParsedText && isLastTokenCompletable))

    const suggestedLabels = []
    const suggestionTokens =
      !hasNonParsedText && isLastTokenCompletable ? expected.prelast || expected.last || [] : expected.last || []

    createSuggestions(suggestionTokens, canBeCompleted).forEach(suggestion => {
      suggestedLabels.push(suggestion.label)
      observer.next(suggestion)
    })

    if (canBeCompleted && suggestionTokens === expected.prelast) {
      createSuggestions(expected.last || [])
        .filter(suggestion => !suggestedLabels.includes(suggestion.label))
        .forEach(suggestion => observer.next(suggestion))
    }

    observer.complete()
  })
}
